import React from 'react'
import { useIntl } from 'react-intl'

import { isValid } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import styled, { useTheme } from 'styled-components/native'

import { getDateFromAppointment } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getDateFromAppointment'
import { SESSION_TYPE_MESSAGES } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/messages'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { DBTTeensSessionTypeIcon } from '../../molecules/dbtTeensSessionTypeIcon/DBTTeensSessionTypeIcon'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

const SessionCard = styled.View(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.backgroundPrimary,
  borderColor: theme.colors.borderDefault,
  borderRadius: theme.spacing['16px'],
  borderWidth: 1,
  flexDirection: 'row',
  gap: theme.spacing['16px'],
  minHeight: 104,
  padding: theme.spacing['16px'],
}))

const SessionDetails = styled.View(({ theme }) => ({
  flex: 1,
  gap: theme.spacing['2px'],
  minWidth: 0,
}))

export type DBTTeensSessionConfirmationCardProps = {
  appointment: Pick<
    Appointment,
    'appointmentDuration' | 'meetingFormat' | 'sessionType' | 'startDate' | 'startTime' | 'timeZone'
  >
  attendeeNames: string[]
  sessionNumber: number
  sessionTypeLabel?: string
}

const getDateTimeLabel = (appointment: Pick<Appointment, 'startDate' | 'startTime' | 'timeZone'>): string => {
  try {
    const appointmentDate = getDateFromAppointment(appointment)
    if (!isValid(appointmentDate)) {
      return ''
    }
    return formatInTimeZone(appointmentDate, appointment.timeZone, 'EEE, MMM d · h:mm a zzz')
  } catch {
    return ''
  }
}

export const DBTTeensSessionConfirmationCard: React.FC<DBTTeensSessionConfirmationCardProps> = ({
  appointment,
  attendeeNames,
  sessionNumber,
  sessionTypeLabel = 'Session',
}) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const dateTimeLabel = getDateTimeLabel(appointment)
  const sessionTitle = [`Session ${sessionNumber}`, dateTimeLabel].filter(Boolean).join(' · ')
  const sessionMetadata = [sessionTypeLabel, attendeeNames.join(', ')].filter(Boolean).join(' · ')
  const meetingFormatMessage = SESSION_TYPE_MESSAGES[appointment.meetingFormat]
  const meetingFormat = meetingFormatMessage ? intl.formatMessage(meetingFormatMessage) : appointment.meetingFormat
  const meetingDetails = [meetingFormat, `${appointment.appointmentDuration} min`].filter(Boolean).join(' · ')

  return (
    <SessionCard testID={tID('SessionSelectionConfirmation-dbt-teens-card')}>
      <DBTTeensSessionTypeIcon sessionType={appointment.sessionType} />
      <SessionDetails>
        <BodyText medium wrap>
          {sessionTitle}
        </BodyText>
        <BodyText color={colors.textSecondary} size={BodyTextSize.SMALL} wrap>
          {sessionMetadata}
        </BodyText>
        <BodyText color={colors.textSecondary} size={BodyTextSize.SMALL}>
          {meetingDetails}
        </BodyText>
      </SessionDetails>
    </SessionCard>
  )
}
